import React from 'react';
import { Divider, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useLang } from '~/hooks/useLang';
import { none, type State} from '@hookstate/core';
import useVehicleState from '~/features/Vehicle/stores/VehicleState';
import { removeMask } from '~/features/Dispatch/utils/stringsManipulations';
import SearchIcon from '@mui/icons-material/Search';
import NameField from '../NameField';
import PhoneField from '../PhoneField';
import CpfFieldSearchMember from '../CpfFieldSearchMember';
import RegistrarionFieldSearchMember from '../RegistrationFieldSearchMember';
import ButtonPatrolmans from '../../../DisplayDispatchDevice/components/ButtonPatrolmans';
import type { CrewFormConnectDeviceProps, IUserCrew } from '~/features/Device/TabListDevice/interfaces/IListDevicesprops';
import ShowPositionCrew from './components/showPositionCrew';
import { createDefaultPatrolman} from '../../constants'


// eslint-disable-next-line max-lines-per-function
export default function CrewFormConnectDevice({ labelCommander, deviceId, labelDriver, labelPatrolmans, commander, driver, patrolmans, error, direction = 'column', requiredFields }: CrewFormConnectDeviceProps) {
  const { translate } = useLang();
  const { searchComponent } = useVehicleState();

  const addPatrol = () => {
    patrolmans?.merge([createDefaultPatrolman()]);
  }

  const handleSearchMembers = (member: State<IUserCrew>) => {
    const position = member.position.get() ?? ''

    if (error[position].get()) {
      if (error[position].get().cpf.length) {
        return;
      }
    }

    searchComponent({
      cpf: removeMask(member.cpf.get() ?? ''),
      registration: member.registration.get()
    })
      .then((response) => {
        if (response.alreadyConnected) {
          if (response.device) {
            if (deviceId.get() != response.device.id) {
              error[position].cpf.set(`${response.representation_name} ${translate('already logged in at')} ${response.device.name}`)
              member.set({ position, cpf: '', name: '', representation_name: '', phone: '', registration: '' })
              return
            }
          } else {
            error[position].cpf.set(`${response.representation_name} ${translate('already logged in at')} ${translate('a station')}`)
            member.set({ position, cpf: '', name: '', representation_name: '', phone: '', registration: '' })
            return
          }
        }

        if (response.phone) {
          error[position].phone.set('')
        }
        member.set({
          position,
          cpf: response.cpf,
          name: response.name,
          representation_name: response.representation_name,
          phone: response.phone,
          registration: response.registration,
        });
      })
      .catch(err => {
        error[position].cpf.set(`${translate(err)}`)
      })
  }

  const handleKeyDown = (event, crew: State<IUserCrew>) => {
    if (event.code === "NumpadEnter" || event.code === "Enter") {
      handleSearchMembers(crew)
    }
  }

  return (
    <Grid item container xs={12} display={'flex'} flexDirection={direction} spacing={2}>
      <Grid item container xs={direction === 'column' ? 12 : 6}>
        <Grid item xs={12} sx={{ pb: 2 }}>
          {direction === 'column' && <Divider sx={{ pt: 2 }} />}
          <Typography sx={{ pt: 1 }}>
            <Tooltip title={translate('Press enter or click the search button to search for a component')}>
              <InfoOutlinedIcon fontSize='small' sx={{ mr: 1, color: '#2196f3' }} />
            </Tooltip>
            <ShowPositionCrew
              error={error.get()}
              typePosition='commander'
              labelCommander={labelCommander}
            />
          </Typography>
        </Grid>

        <Grid item container xs={12} display={'flex'} direction={'row'} spacing={2}>
          <Grid item xs={6}>
            <CpfFieldSearchMember
              onKeyDown={(event) => {
                handleKeyDown(event, commander);
              }}
              stateName="commander"
              memberState={commander}
              error={error} />
          </Grid>
          <Grid item xs={5}>
            <RegistrarionFieldSearchMember
              onKeyDown={(event) => {
                handleKeyDown(event, commander);
              }}
              stateName="commander"
              memberState={commander}
              error={error} />
          </Grid>
          <Grid item xs={1}>
            <IconButton
              aria-label="search members"
              onClick={() => handleSearchMembers(commander)}
              edge="end"
              title={translate('Search')}
            >
              <SearchIcon />
            </IconButton>
          </Grid>

          <Grid item container xs={12} display={'flex'} direction={'row'} spacing={2}>
            <Grid item xs={6}>
              <NameField id="commander-name" nameState={commander?.representation_name} />
            </Grid>
            <Grid item xs={6} >
              <PhoneField
                stateName="commander"
                memberState={commander ?? null}
                error={error}
                required={requiredFields.phone.get()}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Grid item container xs={direction === 'column' ? 12 : 6}>
        <Grid item xs={12} sx={{ pb: 2 }}>
          {direction === 'column' && <Divider sx={{ pt: 2 }} />}
          <Typography sx={{ pt: 1 }}>
            <ShowPositionCrew
              error={error.get()}
              typePosition='driver'
              labelDriver={labelDriver}
            />
          </Typography>
        </Grid>

        <Grid item container xs={12} display={'flex'} direction={'row'} spacing={2}>
          <Grid item xs={6}>
            <CpfFieldSearchMember
              onKeyDown={(event) => {
                handleKeyDown(event, driver);
              }}
              stateName="driver"
              memberState={driver}
              error={error} />
          </Grid>
          <Grid item xs={5}>
            <RegistrarionFieldSearchMember
              onKeyDown={(event) => {
                handleKeyDown(event, driver);
              }}
              stateName="driver"
              memberState={driver}
              error={error} />
          </Grid>
          <Grid item xs={1}>
            <IconButton
              aria-label="search members"
              onClick={() => handleSearchMembers(driver)}
              edge="end"
              title={translate('Search')}
            >
              <SearchIcon />
            </IconButton>
          </Grid>
        </Grid>

        <Grid item xs={12} sx={{ pt: 2 }}>
          <NameField id="driver" nameState={driver.representation_name} />
        </Grid>
      </Grid>

      <Grid item container xs={12} display={'flex'} direction={'row'}>
        <Grid item xs={direction === 'column' ? 11 : 5.5} sx={{ pb: 2 }}>
          {direction === 'column' && <Divider sx={{ pt: 2 }} />}
          <Typography sx={{ pt: 2 }}>
            <ShowPositionCrew
              error={error.get()}
              typePosition='patrolman'
              labelPatrolmans={labelPatrolmans}
            />
          </Typography>
        </Grid>
        <Grid item xs={direction === 'column' ? 1 : 5} sx={{ width: '100%', display: 'flex' }}>
          <ButtonPatrolmans
            tooltipName={labelPatrolmans ? `Adicionar ${labelPatrolmans.toLocaleLowerCase()}` : translate('Add patrolman')}
            sxProps={{
              ml: 1,
              mt: 2,
              transform: 'scale(0.5)',
              color: 'white',
              backgroundColor: 'primary.main',
              // backgroundColor: '#009688',
              "&:hover": {
                backgroundColor: "primary.dark",
                // backgroundColor: "#00544a"
              }
            }}
            handleOnClick={addPatrol}
            isAdd
            disabled={!!error.patrolmans.cpf.get()} />
        </Grid>
      </Grid>
      {
        !patrolmans.get().length &&
        <Grid item sx={{ alignContent: 'center', pb: 3, color: 'orange' }}>
          <span>
            {labelPatrolmans ? `Sem ${labelPatrolmans.toLocaleLowerCase()} informado(a)` : translate('No patrolmans')}
          </span>
        </Grid>
      }
      <Grid item container direction="row" xs={12} >
        <ul style={{ margin: 0, padding: 0 }}>
          {patrolmans.get().map((_, index) => (<li key={index.toString()}
            style={{
              display: 'flex',
              justifyContent: 'left',
              alignItems: 'center',
            }}>
            <Grid item xs={3} sx={{ pb: 2 }}>
              <CpfFieldSearchMember
                onKeyDown={(event) => {
                  handleKeyDown(event, patrolmans[index]);
                }}
                stateName="patrolmans"
                memberState={patrolmans[index]}
                error={error} />
            </Grid>
            <Grid item xs={3} sx={{ pl: 1, pb: 2 }}>
              <RegistrarionFieldSearchMember
                onKeyDown={(event) => {
                  handleKeyDown(event, patrolmans[index]);
                }}
                stateName="patrolmans"
                memberState={patrolmans[index]}
                error={error} />
            </Grid>
            <Grid item xs={5} sx={{ pl: 1, pb: 2 }}>
              <NameField id="patrolmans-name" nameState={patrolmans[index].representation_name} />
            </Grid>
            {(patrolmans[index].cpf.get()?.length || patrolmans[index].registration.get()?.length)
              && !error.patrolmans.cpf.get()
              && !patrolmans[index].representation_name.get()
              ? (
                <Grid item xs={1} sx={{ pl: 1, pb: 2 }}>
                  <IconButton
                    title={translate('Search')}
                    aria-label="search members"
                    onClick={() => handleSearchMembers(patrolmans[index])}
                    edge="end"
                  >
                    <SearchIcon />
                  </IconButton>
                </Grid>
              ) : (
                <Grid item xs={1} sx={{ pl: 1, pb: 2 }}>
                  <ButtonPatrolmans
                    tooltipName={translate('Remove patrolman')}
                    sxProps={{
                      transform: 'scale(0.5)',
                      color: '#black',
                      backgroundColor: '#9e9e9e',
                      "&:hover": { backgroundColor: "#6e6e6e" }
                    }}
                    handleOnClick={() => {
                      error.patrolmans.cpf.set('');
                      patrolmans[index].set(none);
                    }} />
                </Grid>
              )}
          </li>))}
        </ul>
      </Grid>
    </Grid >
  )
}